<template>
  <v-card class="rounded-xl mt-8" :border="false" :elevation="0">
    <div class="d-flex flex-column flex-sm-row align-sm-center justify-space-between">
      <v-text-field
        v-model.trim="search"
        v-model="search"
        :placeholder="$t('COMMON.FILTER.SEARCH')"
        hide-details
      />
      <div
        class="actions d-flex align-center justify-sm-center justify-start ml-0 ml-sm-4 mt-4 mt-sm-0"
      >
        <v-tooltip location="bottom">
          {{ $t('COMMON.FILTER.SHOW_FILTERS') }}
          <template v-slot:activator="{ props }">
            <img
              alt="icône paramètre"
              class="mr-4"
              src="@/assets/icons/accounts/config.svg"
              v-bind="props"
              @click="showFilter = !showFilter"
            />
          </template>
        </v-tooltip>
        <img alt="icône plein écran" src="@/assets/icons/accounts/screen.svg" />
      </div>
    </div>

    <v-row class="mt-8 mb-0" v-if="showFilter">
      <v-col cols="12" md="4" class="py-0 mt-8 mt-md-0">
        <v-select
          id="select-role"
          v-model="selectedRole"
          :items="roles"
          :no-data-text="$t('COMMON.NO_DATA')"
          :placeholder="$t('ACCOUNT_PAGE.PLACEHOLDER.ROLE')"
          hide-details
          item-title="title"
          item-value="title"
        >
          <template v-slot:selection="{ item }">
            <v-chip size="small">
              <span>{{ item.title }}</span>
            </v-chip>
          </template>
          <template v-slot:item="{ item, props }">
            <v-list-item :title="item.title" :value="item.title" v-bind="props">
              <v-tooltip
                v-if="shouldShowTooltip(item, 'select-role')"
                activator="parent"
                location="bottom left"
              >
                {{ item.title }}
              </v-tooltip>
            </v-list-item>
          </template>
        </v-select>
      </v-col>
      <v-col cols="12" md="4" class="py-0 mt-8 mt-md-0">
        <v-select
          id="select-status"
          v-model="selectedStatus"
          :items="status"
          :no-data-text="$t('COMMON.NO_DATA')"
          :placeholder="$t('ACCOUNT_PAGE.PLACEHOLDER.STATUS')"
          hide-details
          required
        >
          <template v-slot:selection="{ item }">
            <v-chip size="small">
              <span>{{ item.title }}</span>
            </v-chip>
          </template>
          <template v-slot:item="{ item, props }">
            <v-list-item v-bind="props">
              <v-tooltip
                v-if="shouldShowTooltip(item, 'select-role')"
                activator="parent"
                location="bottom left"
              >
                {{ item.title }}
              </v-tooltip>
            </v-list-item>
          </template>
        </v-select>
      </v-col>
    </v-row>

    <v-table hover class="mt-8">
      <thead>
        <tr>
          <th id="role_title" @click="sortColumns('role_title')" style="min-width: 250px">
            <div class="d-flex align-center justify-space-between">
              <span class="font-weight-bold">{{ $t('ACCOUNT_PAGE.TABLE.ROLE') }}</span>
              <SortArrow :order="order" :sort="sort" value="role_title" />
            </div>
          </th>
          <th id="id" @click="sortColumns('id')" style="width: 80px; min-width: 80px">
            <div class="d-flex align-center justify-space-between">
              <span class="font-weight-bold">{{ $t('ACCOUNT_PAGE.TABLE.ID') }}</span>
              <SortArrow :order="order" :sort="sort" value="id" />
            </div>
          </th>
          <th id="email" @click="sortColumns('email')" style="min-width: 250px">
            <div class="d-flex align-center justify-space-between">
              <span class="font-weight-bold">{{ $t('ACCOUNT_PAGE.TABLE.USER') }}</span>
              <SortArrow :order="order" :sort="sort" value="email" />
            </div>
          </th>
          <th id="applicant-email" @click="sortColumns('applicant_email')" style="min-width: 250px">
            <div class="d-flex align-center justify-space-between">
              <span class="font-weight-bold">{{ $t('ACCOUNT_PAGE.TABLE.APPLICANT') }}</span>
              <SortArrow :order="order" :sort="sort" value="applicant_email" />
            </div>
          </th>
          <th id="numberFiles" style="width: 150px; min-width: 150px">
            <div class="d-flex align-center justify-space-between">
              <span class="font-weight-bold">{{ $t('ACCOUNT_PAGE.TABLE.FILE') }}</span>
            </div>
          </th>
          <th id="groups" style="min-width: 250px">
            <div class="d-flex align-center justify-space-between">
              <span class="font-weight-bold">{{ $t('ACCOUNT_PAGE.TABLE.GROUPS') }}</span>
            </div>
          </th>
          <th id="status" @click="sortColumns('status')" style="width: 150px; min-width: 150px">
            <div class="d-flex align-center justify-space-between">
              <span class="font-weight-bold">{{ $t('ACCOUNT_PAGE.TABLE.STATUS') }}</span>
              <SortArrow :order="order" :sort="sort" value="status" />
            </div>
          </th>
          <th id="infoQuality" style="width: 150px; min-width: 150px">
            <div class="d-flex align-center justify-space-between">
              <span class="font-weight-bold">{{ $t('ACCOUNT_PAGE.TABLE.INFO_QUALITY') }}</span>
            </div>
          </th>
          <th id="description" style="width: 150px; min-width: 150px">
            <div class="d-flex align-center justify-space-between">
              <span class="font-weight-bold">{{ $t('ACCOUNT_PAGE.TABLE.DESCRIPTION') }}</span>
            </div>
          </th>
          <th id="sub_status" style="width: 150px; min-width: 150px">
            <div class="d-flex align-center justify-space-between">
              <span class="font-weight-bold">{{ $t('ACCOUNT_PAGE.TABLE.SUB_STATUS') }}</span>
            </div>
          </th>
          <th id="date" @click="sortColumns('date')" style="width: 115px; min-width: 115px">
            <div class="d-flex align-center justify-space-between">
              <span class="font-weight-bold">{{ $t('ACCOUNT_PAGE.TABLE.ACTIVATED_DATE') }}</span>
              <SortArrow :order="order" :sort="sort" value="date" />
            </div>
          </th>
          <th id="actions" style="width: 60px" />
        </tr>
      </thead>
      <tbody>
        <tr v-if="loading">
          <td class="pa-8 pa-md-16 text-md-center" colspan="10">
            {{ $t('ACCOUNT_PAGE.TABLE.LOADING_USER') }}
            <v-progress-linear class="my-5" color="#165039" indeterminate />
          </td>
        </tr>
        <tr v-else-if="!accounts.length">
          <td class="pa-8 pa-md-16 text-md-center" colspan="10">
            {{ $t('ACCOUNT_PAGE.TABLE.NO_USER') }}
            <div class="d-flex align-center justify-center mt-8">
              <img src="@/assets/images/empty.svg" width="250" alt="Empty" />
            </div>
          </td>
        </tr>
        <tr
          v-for="account of accounts"
          v-else
          :key="account.technical_id"
          @click.stop.prevent="detail(account)"
        >
          <td class="font-weight-bold">
            {{ getFormattedRole(account.roles) }}
          </td>
          <td class="color-light">
            {{ account.technical_id }}
          </td>
          <td class="font-weight-bold">
            {{ account.email }}
          </td>
          <td class="font-weight-bold">
            {{ account.applicant_email }}
          </td>
          <td class="font-weight-bold">
            <!--#TODO BOOM-104 ADD NUMBER OF FILES-->
          </td>
          <td>
            <template v-for="(group, index) of account.groups" :key="index">
              <span v-if="index < 1" class="font-weight-bold">{{ group.name }}</span>
              <span v-if="index === 1" class="text-grey">
                ( +{{ account.groups.length - 1 }}
                {{
                  account.groups.length - 1 === 1
                    ? $t('ACCOUNT_PAGE.TABLE.OTHER')
                    : $t('ACCOUNT_PAGE.TABLE.OTHERS')
                }}
                )
              </span>
            </template>
          </td>
          <td>
            <div class="d-flex align-center justify-space-between">
              <span class="font-weight-bold">
                {{ $t('STATUS.' + getStatusName(account.status)) }}
              </span>
            </div>
          </td>
          <td>
            <div class="d-flex align-center justify-space-between">
              <span
                class="status-dot"
                :style="{ 'background-color': getInfoQualityDotColor(account.info_quality) }"
              ></span>
            </div>
          </td>
          <td class="font-weight-bold">
            {{ account.description ? getShortString(account.description) : '-' }}
          </td>
          <td class="font-weight-bold">
            {{ account.sub_status ? getShortString(account.sub_status) : '-' }}
          </td>
          <td class="color-light">
            {{ getFormattedDate(account.activated_date) }}
          </td>
          <td class="text-right" @click.stop.prevent>
            <v-menu>
              <template v-slot:activator="{ props }">
                <svg-icon
                  v-bind="props"
                  @click.stop.prevent
                  class="icon"
                  type="mdi"
                  :path="dotVertical"
                />
              </template>
              <v-list elevation="1">
                <v-list-item link :to="'/accounts/detail/' + `${account.technical_id}`">
                  <v-list-item-title>
                    {{ $t('COMMON.BUTTON.DETAIL') }}
                  </v-list-item-title>
                </v-list-item>
                <v-list-item link :to="'/accounts/update/' + `${account.technical_id}`">
                  <v-list-item-title>
                    {{ $t('COMMON.BUTTON.UPDATE') }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </td>
        </tr>
      </tbody>
    </v-table>

    <div v-if="accounts.length" class="mt-8 d-flex align-center justify-center">
      <p class="load-more color-light" @click="loadMore()">{{ $t('COMMON.BUTTON.LOAD_MORE') }}</p>
    </div>
  </v-card>
</template>

<script>
import SortArrow from '@/components/tables/SortArrow.vue'
import SvgIcon from '@jamescoyle/vue-icon'
import { mdiDotsVertical } from '@mdi/js'
import AccountStatus from '@/constants/accountStatus'
import RoleService from '@/services/RoleService'
import AccountService from '@/services/AccountService'
import { handleResponseError, shouldShowTooltip } from '@/components/utils/globalFunction'
import AccountInfoQuality from '@/constants/accountInfoQuality'

export default {
  name: 'AccountList',
  components: { SortArrow, SvgIcon },
  data() {
    return {
      dotVertical: mdiDotsVertical,
      loading: false,
      search: '',
      awaitingSearch: false,
      sort: 'email',
      order: 'desc',
      page: 1,
      pages: null,
      limit: 12,
      accounts: [],
      loadingExport: false,
      roles: [],
      status: [
        { title: this.$t('STATUS.PENDING'), value: 'pending' },
        { title: this.$t('STATUS.INACTIVE'), value: 'inactive' },
        { title: this.$t('STATUS.ACTIVE'), value: 'active' }
      ],
      selectedRole: null,
      selectedStatus: null,
      showFilter: false,
      filters: {}
    }
  },
  async created() {
    await this.getAccountList()

    try {
      const responseRole = await RoleService.getRoleList()
      this.roles = responseRole.item_list
    } catch (error) {
      handleResponseError(error)
    }
  },
  watch: {
    search() {
      if (!this.awaitingSearch) {
        setTimeout(() => {
          this.getAccountList()
          this.awaitingSearch = false
        }, 1000)
      }
      this.awaitingSearch = true
    },
    selectedRole() {
      if (this.selectedRole) {
        this.filters = { ...this.filters, role: this.selectedRole }
      } else {
        delete this.filters.role
      }
      this.getAccountList()
    },
    selectedStatus() {
      if (this.selectedStatus) {
        this.filters = { ...this.filters, status: this.selectedStatus }
      } else {
        delete this.filters.status
      }
      this.getAccountList()
    }
  },
  methods: {
    shouldShowTooltip,
    async getAccountList() {
      this.loading = true
      try {
        const response = await AccountService.getPaginatedAccountList(
          this.search,
          this.sort,
          this.order,
          this.page,
          this.limit,
          this.filters
        )
        this.accounts = response.data
        this.pages = response.max_page
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    },
    detail(account) {
      this.$router.push('/accounts/detail/' + account.technical_id)
    },
    getShortString(value) {
      let formatString = value.substring(0, 20)
      if (value.length > 20) {
        formatString = formatString + '...'
      }
      return formatString
    },
    sortColumns(sortParam) {
      this.page = 1
      this.sort = sortParam
      if (this.order === 'asc') {
        this.order = 'desc'
      } else {
        this.order = 'asc'
      }
      this.getAccountList()
    },
    getStatusName(status) {
      return AccountStatus[status]?.name
    },
    getInfoQualityDotColor(status) {
      return AccountInfoQuality[status]?.dotColor
    },
    getFormattedRole(roles) {
      return roles[0].title
    },
    getFormattedDate(date) {
      if (date) {
        return new Date(date).toLocaleDateString()
      } else {
        return '-'
      }
    },
    async loadMore() {
      if (this.page < this.pages) {
        this.page = this.page + 1
        const response = await AccountService.getPaginatedAccountList(
          this.search,
          this.sort,
          this.order,
          this.page,
          this.limit,
          this.filters
        )
        this.accounts.push(...response.data)
      }
    }
  }
}
</script>

<style scoped>
.v-card {
  padding: 32px;
}

.v-card .actions img:hover {
  cursor: pointer;
}

:deep(.v-table__wrapper) {
  border-radius: 20px !important;
}

thead {
  background-color: #165039 !important;
}

th > * {
  color: white;
}

tr:nth-of-type(even) {
  background-color: #fafafa;
}

td {
  border-bottom: unset !important;
}

td:hover {
  cursor: pointer;
}

.color-light {
  color: #bfbfbf;
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.load-more {
  cursor: pointer;
}
</style>
